import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Alert, ModalBody, ModalFooter } from 'reactstrap';
import type { OneOffChannel, SerializedAssetPublish } from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useCreateAssetPublishMutation, useDeleteAssetPublishMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { publishMessages } from './publishMessages';
import { ONE_OFF_CHANNEL_NAMES, publishBadgeText, publishErrorText } from './publishBadgeText';
import { manualHowTo } from './manualHowTo';
import { trackAssetDownloaded } from '../assetEvents';

interface PublishManualPanelProps {
  accountId: number;
  kitAssetUuid: string;
  channel: OneOffChannel;
  record: SerializedAssetPublish | null;
  downloadUrl: string | null;
  /**
   * Instagram: why the API cannot post the file as a Reel. Then the panel says why and the mark is
   * the fallback (`manual: true`). Null for a manual channel.
   */
  fallbackReason?: string | null;
  onClose: () => void;
}

/**
 * A manual channel (Spotify Canvas, Apple Music motion artwork, a profile banner, the YouTube
 * thumbnail, Instagram): no API takes the file, so the artist downloads it, publishes it in the
 * platform's own tool and marks it here. The mark is the binary badge. The steps and the labels
 * "I added it" / "I set it" / "Undo the mark" follow design assets-rethink b3; no reminder.
 */
const PublishManualPanelComponent: React.FC<PublishManualPanelProps> = (props) => {
  const { accountId, kitAssetUuid, channel, record, downloadUrl, fallbackReason = null, onClose } = props;
  const intl = useIntl();
  const [mark, markState] = useCreateAssetPublishMutation();
  const [unmark, unmarkState] = useDeleteAssetPublishMutation();
  const isMarked = record?.status === 'published';
  const error = markState.error ?? unmarkState.error;
  const howTo = manualHowTo(channel);
  const name = ONE_OFF_CHANNEL_NAMES[channel];

  const toggle = useCallback(async () => {
    const result =
      isMarked && record !== null
        ? await unmark({ accountId, publishUuid: record.uuid })
        : await mark({
            accountId,
            body: { kitAssetUuid, channel, timing: 'now', ...(fallbackReason === null ? {} : { manual: true }) },
          });
    if (isSuccessfulResult(result)) {
      onClose();
    }
  }, [accountId, channel, fallbackReason, isMarked, kitAssetUuid, mark, onClose, record, unmark]);

  return (
    <>
      <ModalBody className="d-flex flex-column gap-3" data-testid="publish-manual">
        {howTo.title !== null && <strong>{intl.formatMessage(howTo.title, { channel: name })}</strong>}
        <span className="text-muted">{publishBadgeText(intl, channel, record).text}</span>
        {fallbackReason !== null && <span data-testid="publish-refusal">{fallbackReason}</span>}
        <span>
          {fallbackReason !== null
            ? intl.formatMessage(publishMessages.refusalFallback)
            : intl.formatMessage(howTo.lead, { channel: name })}
        </span>
        {howTo.steps.length > 0 && (
          // design: assets-rethink b3 `.howto`
          <ol className="mb-0 ps-3 d-flex flex-column gap-1" data-testid="publish-howto">
            {howTo.steps.map((step) => (
              <li key={step.id}>{intl.formatMessage(step, { channel: name })}</li>
            ))}
          </ol>
        )}
        {howTo.steps.length > 0 && (
          <small className="text-muted">{intl.formatMessage(publishMessages.howToNoReminder)}</small>
        )}
        {error !== undefined && (
          <Alert color="danger" className="mb-0" data-testid="publish-error">
            {publishErrorText(intl, channel, error)}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter className="flex-wrap gap-2">
        {downloadUrl !== null && (
          <a
            className="btn btn-outline-secondary"
            href={downloadUrl}
            download
            onClick={() => trackAssetDownloaded(undefined, 'manual')}
          >
            {intl.formatMessage(publishMessages.actionDownload)}
          </a>
        )}
        <SMLButton
          color={isMarked ? 'secondary' : 'primary'}
          outline={isMarked}
          omitCap
          disabled={markState.isLoading || unmarkState.isLoading}
          onClick={() => toggle().catch(console.error)}
        >
          {isMarked
            ? intl.formatMessage(publishMessages.actionUndoMark)
            : intl.formatMessage(howTo.markLabel, howTo.markNamesChannel ? { channel: name } : undefined)}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

export const PublishManualPanel = TypedMemo(PublishManualPanelComponent);
